import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { PaymentGroup } from '../../../../../models/entities/payment-group';
import { PaymentDetailDialogComponent, PaymentDetailDialogInput } from '../../../shared/payment-detail-dialog/payment-detail-dialog.component';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { LegFiJwtService } from '../../../../../services/auth/legfi-jwt.service';
import { JwtLegFiClaims } from '../../../../../services/auth/jwt-legfi-claims.model';
import { PaymentGroupsService } from '../../../../../services/payment/payment-groups.service';
import { GrowlerService } from '../../../../../services/growler.service';
import { GenericMatDialogComponent, GenericMatDialogInput } from '../../../shared/generic-mat-dialog/generic-mat-dialog.component';
import { GenericDialogResult } from '../../../shared/generic-mat-dialog/generic-dialog-result';
import { AuditHistoryComponent, AuditHistoryDialogInput } from '../../../shared/audit-history/audit-history.component';
import { ArchiveService } from '../../../../../services/archive.service';
import { ConfirmWithInputDialogComponent, ConfirmWithInputDialogInput, ConfirmWithInputDialogResult } from '../../../shared/confirm-with-input-dialog/confirm-with-input-dialog.component';
import { ConfirmActionDialogComponent, ConfirmActionDialogInput } from '../../../shared/confirm-action-dialog/confirm-action-dialog.component';
import { ValidationMessageService } from '../../../../../services/validation-message.service';
import { DeleteOfflinePaymentDialogComponent, DeleteOfflinePaymentDialogInput, DeleteOfflinePaymentDialogResult } from 'app/components/app-layout/shared/delete-offline-payment-dialog/delete-offline-payment-dialog.component';
import { MatDialogSizes } from 'app/enums/mat-dialog-sizes.enum';
import { EditPaymentDialogComponent, EditPaymentDialogInput } from 'app/components/app-layout/payments/incoming-payments/edit-payment-dialog/edit-payment-dialog.component';
import { EditPaymentDialogModule } from 'app/components/app-layout/payments/incoming-payments/edit-payment-dialog/edit-payment-dialog.module';

@Component({
    selector: 'app-single-payment-actions',
    templateUrl: './single-payment-actions.component.html',
})
export class SinglePaymentActionsComponent implements OnInit
{

    jwt: JwtLegFiClaims;
    @Input() paymentGroup: PaymentGroup;
    @Output() reload: EventEmitter<void> = new EventEmitter<void>();

    hasWriteAccess: boolean = false;

    constructor(
            private _archive: ArchiveService,
            private _dialog: MatDialog,
            private _paymentGroups: PaymentGroupsService,
            private _growler: GrowlerService,
    ) {
    }

    ngOnInit(): void {
        this.jwt = LegFiJwtService.read();
        this.hasWriteAccess = LegFiJwtService.doesUserHaveModulePermission(
                'payments',
                true,
        );
    }

    refundPayment() {
        const data: GenericMatDialogInput = {
            title: 'Refund Payment',
            text: 'Are you sure you wish to refund the selected payment?',
            buttonText: 'Yes, Refund Payment',
        };
        this._dialog.open(GenericMatDialogComponent, {
            data: data,
        }).afterClosed().subscribe((result: GenericDialogResult) => {
            if (result.success) {
                this._paymentGroups.refundPayment(this.jwt.orgId, this.paymentGroup.id).subscribe({
                    next: () => {
                        this._growler.success(
                                'Success',
                                'Payment successfully refunded.',
                        );

                        this.reload.emit();
                    },
                    error: (err) => {
                        this._growler.error(
                                'Error',
                                'There was a problem refunding your payment.',
                        );
                        this.reload.emit();
                    },
                });
            }
        });
    }

    showPaymentDetail() {
        this._dialog.open(PaymentDetailDialogComponent, {
            data: <PaymentDetailDialogInput>{
                paymentGroupId: this.paymentGroup.id,
            },
        });
    }

    deletePayment() {
        const data: ConfirmWithInputDialogInput = {
            title: 'Delete Payment',
            content: 'Are you sure you wish to remove the selected payment?',
            label: 'Reason for delete',
            inputRequired: false,
            btnContent: 'Yes, Remove Payment',
        };
        this._dialog.open(ConfirmWithInputDialogComponent, {
            data: data,
        }).afterClosed().subscribe((result: ConfirmWithInputDialogResult) => {
            if (result.success) {
                this._paymentGroups
                        .deletePaymentGroups(
                                null,
                                [this.paymentGroup.id],
                                result.value,
                        )
                        .subscribe(
                                () => {
                                    this._growler.success(
                                            'Success',
                                            'Payment successfully deleted.',
                                    );

                                    this.reload.emit();
                                },
                                () => {
                                    this._growler.error(
                                            'Error',
                                            'There was a problem deleting your payment.',
                                    );
                                },
                        );
            }
        });
    }

    deleteOfflinePaymentGroup() {
        const data: DeleteOfflinePaymentDialogInput = {
            paymentGroups: [this.paymentGroup],
        };
        this._dialog.open(DeleteOfflinePaymentDialogComponent, {
            data: data,
        }).afterClosed().subscribe((result: DeleteOfflinePaymentDialogResult) => {
            if (result.success) {
                this.reload.emit();
            }
        });
    }

    archivePayment() {
        const data: GenericMatDialogInput = {
            title: 'Archive Payment?',
            text: 'Archived payments will no longer appear anywhere and cannot be restored. Are you sure you wish to archive this payment?',
            buttonText: 'Yes, archive it',
        };
        this._dialog.open(GenericMatDialogComponent, {
            data: data,
        }).afterClosed().subscribe((result: GenericDialogResult) => {
            if (result.success) {
                this._archive.archiveRecords(null, 'paymentGroup', [this.paymentGroup.id]).subscribe(
                        () => {
                            this.reload.emit();
                        },
                        () => {
                            this._growler.error('Error Archiving Payments', `Failed to archive some payments`);
                        },
                );
            }
        });
    }

    showPaymentHistory() {
        const data: AuditHistoryDialogInput = {
            model: 'PaymentGroup',
            id: this.paymentGroup.id,
        };
        this._dialog.open(AuditHistoryComponent, {
            width: MatDialogSizes.LG,
            data: data,
        });
    }

    editPayment() {
        const data: EditPaymentDialogInput = {
            paymentGroup: this.paymentGroup,
        };
        this._dialog.open(EditPaymentDialogComponent, {
            data: data,
            width: MatDialogSizes.MD,
        }).afterClosed().subscribe({
            next: (result: GenericDialogResult) => {
                if (result?.success) {
                    this.reload.emit();
                }
            },
        });
    }

    sendReceipts() {
        const data: ConfirmActionDialogInput = {
            title: 'Resend Payment Receipt',
            content: `Are you sure you wish to resend a receipt for this payment?`,
            confirmButton: 'Yes, send receipt',
        };
        this._dialog.open(ConfirmActionDialogComponent, {
            data: data,
        }).afterClosed().subscribe({
            next: (result: ConfirmWithInputDialogResult) => {
                if (result?.success) {
                    this._paymentGroups.sendReceiptsForPaymentGroup([this.paymentGroup.id])
                            .subscribe({
                                next: () => {
                                    this._growler.success('Success', 'Payment receipt is being emailed.');
                                },
                                error: (response) => {
                                    this._growler.error('Oops', 'There was a problem resending the payment receipt.');
                                },
                            });
                }
            },
        });
    }

}
